import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { Canvas, useFrame } from "@react-three/fiber";
import Layout from "../Util/Layout";

const PARTICLE_COLOR = "#556cd6"; // matches theme.palette.primary.main

// A loose 3D cloud of points that slowly drifts on two axes. Lives inside an absolutely-positioned Canvas
// behind the hero content. Honors prefers-reduced-motion by holding still.
const ParticleField = ({ count = 350 }) => {
  const pointsRef = useRef();
  // Normalized mouse position [-1, 1] across the viewport, updated outside of React
  // state so cursor movement doesn't trigger re-renders.
  const pointerRef = useRef({ x: 0, y: 0 });
  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 12;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 12;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 12;
    }
    return arr;
  }, [count]);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    const handlePointer = (e) => {
      pointerRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointerRef.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("pointermove", handlePointer);
    return () => window.removeEventListener("pointermove", handlePointer);
  }, [prefersReducedMotion]);

  useFrame((state) => {
    if (!pointsRef.current || prefersReducedMotion) return;
    const t = state.clock.elapsedTime;
    const { x: px, y: py } = pointerRef.current;
    const targetX = t * 0.015 - py * 0.35;
    const targetY = t * 0.025 + px * 0.45;
    // Ease toward the target so cursor jumps don't snap the field
    pointsRef.current.rotation.x +=
      (targetX - pointsRef.current.rotation.x) * 0.05;
    pointsRef.current.rotation.y +=
      (targetY - pointsRef.current.rotation.y) * 0.05;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        color={PARTICLE_COLOR}
        size={0.05}
        sizeAttenuation
        transparent
        opacity={0.7}
        depthWrite={false}
      />
    </points>
  );
};

const HomePage = () => {
  const [role, setRole] = useState("Technical Product Manager ");
  const [opacity, setOpacity] = useState(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const roles = [
    "Technical Product Manager ",
    "Software Engineer ",
    "Martial Artist ",
    "Cornhusker ",
    "Tinkerer ",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setOpacity(0);
      setTimeout(() => {
        const currentRoleIndex = roles.indexOf(role);
        const nextRoleIndex = (currentRoleIndex + 1) % roles.length;
        setRole(roles[nextRoleIndex]);
        setOpacity(1);
      }, 500);
    }, 3000);

    return () => clearInterval(interval);
  }, [role, roles]);

  const handleRoleChange = () => {
    setOpacity(0);
    setTimeout(() => {
      const currentRoleIndex = roles.indexOf(role);
      const nextRoleIndex = (currentRoleIndex + 1) % roles.length;
      setRole(roles[nextRoleIndex]);
      setOpacity(1);
    }, 500);
  };

  return (
    <Layout>
      <Box
        sx={{
          position: "relative",
          minHeight: "75vh",
          overflow: "hidden",
          borderRadius: 2,
        }}
      >
        {/* Particle hero background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
          }}
        >
          <Canvas
            camera={{ position: [0, 0, 5], fov: 60 }}
            dpr={[1, 1.5]}
            gl={{ antialias: false, powerPreference: "low-power" }}
          >
            <ParticleField />
          </Canvas>
        </Box>

        {/* Soft white vignette so the text stays effortlessly readable against the particles */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background:
              "radial-gradient(ellipse at center, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.55) 45%, rgba(255,255,255,0) 80%)",
          }}
        />

        {/* Hero content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            py: { xs: 6, md: 10 },
            px: { xs: 2, md: 4 },
          }}
        >
          <Typography variant="h2" gutterBottom>
            Hi, my name is <strong>Nick Colleran</strong>
          </Typography>
          <Typography variant="h4" color="textSecondary" paragraph>
            And I am a{" "}
            <Button
              style={{
                fontSize: "inherit",
                fontWeight: "inherit",
                fontFamily: "inherit",
                color: "#007bff",
                padding: 0,
                minWidth: 0,
                background: "none",
                boxShadow: "none",
                transition: "opacity 0.5s",
                opacity,
              }}
              onClick={handleRoleChange}
            >
              {role}
            </Button>{" "}
            out of Lincoln, NE
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component="a"
            href="/Nick_Colleran_Resume.pdf"
            download="Nick_Colleran_Resume.pdf"
            style={{ marginBottom: "20px" }}
          >
            Download My Resume
          </Button>
          <Typography
            variant="h7"
            paragraph
            sx={{ maxWidth: 720, mx: "auto" }}
          >
            I have been working with software for about 6 years, giving me a
            strong foundation in multiple programming languages and frameworks. I
            spend my free time practicing martial arts, preparing for amateur
            bodybuilding shows, working on personal projects, and enjoying the
            outdoors. Currently, I am working with ALA Engineering in Lincoln NE,
            where I am both leading and contributing to our autonomy work —
            steering technical direction across teams while still writing code
            for the autonomy stack myself. Head over to the{" "}
            <strong>Projects</strong> page to see what I've been building, or
            reach out by email (found on the contact form) and I will be in touch
            shortly.
          </Typography>
        </Box>
      </Box>
    </Layout>
  );
};

export default HomePage;
